import type { FindingStatement } from '@/models/report';

const NO_STATEMENTS: FindingStatement[] = [];

/**
 * The statements a grouped section's one input shows: those of every class
 * in the group, in label order. A report ingested while the section was SPLIT
 * keeps MR under MITRAL_VALVE, not under the group's first class.
 */
export function pooledStatements(
  findings: Record<string, FindingStatement[]>,
  classes: readonly string[]
): FindingStatement[] {
  const filled = classes.filter((c) => (findings[c] ?? []).length > 0);
  // One filled class: hand back its own array, so the input's value keeps
  // its identity between renders.
  if (filled.length <= 1) {
    return findings[filled[0] ?? classes[0]] ?? NO_STATEMENTS;
  }
  return filled.flatMap((c) => findings[c] ?? []);
}

/**
 * Splits an edit of a grouped section's input back into its classes. Each
 * statement stays under the class it is stored under; a new one goes to the
 * first class. Returns only the classes whose statements changed, so one edit
 * is one history step.
 */
export function splitPooledChange(
  findings: Record<string, FindingStatement[]>,
  classes: readonly string[],
  next: FindingStatement[]
): [string, FindingStatement[]][] {
  const [first, ...others] = classes;
  const ownerOf = new Map<string, string>();
  others.forEach((c) =>
    (findings[c] ?? []).forEach((s) => ownerOf.set(s.uuid, c))
  );
  const byClass = new Map<string, FindingStatement[]>(
    classes.map((c) => [c, []])
  );
  next.forEach((s) => byClass.get(ownerOf.get(s.uuid) ?? first)!.push(s));

  return classes
    .map((c): [string, FindingStatement[]] => [c, byClass.get(c)!])
    .filter(([c, value]) => {
      const current = findings[c] ?? [];
      return (
        value.length !== current.length ||
        value.some((s, i) => s !== current[i])
      );
    });
}
